import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, MapPin, LogOut, Package, LayoutDashboard } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { usePincode } from '../contexts/PincodeContext';
import { t } from '../lib/translations';
import CartSheet from './CartSheet';
import PincodeModal from './PincodeModal';
import GlobalSearch from './GlobalSearch';

export const Header = () => {
  const navigate = useNavigate();
  const { user, logout, isAdmin, isAuthenticated } = useAuth();
  const { cartCount } = useCart();
  const { currentPincode, areaName } = usePincode();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pincodeModalOpen, setPincodeModalOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="header-sticky" data-testid="main-header">
      <div className="container-app">
        {/* Top bar */}
        <div className="flex items-center justify-between py-2 border-b border-slate-100 text-sm">
          <button
            onClick={() => setPincodeModalOpen(true)}
            className="flex items-center gap-1 text-slate-600 hover:text-green-600 transition-colors"
            data-testid="pincode-selector"
          >
            <MapPin className="w-4 h-4" />
            <span className="hidden sm:inline">{t('deliverTo')}</span>
            <span className="font-medium text-green-600">{areaName || currentPincode}</span>
          </button>
        </div>

        {/* Main header */}
        <div className="flex items-center justify-between py-4 gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="logo">
            <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">AV</span>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-xl font-bold text-slate-900">AV Mart</h1>
              <p className="text-xs text-slate-500">Shamli</p>
            </div>
          </Link>

          {/* Global Search - Desktop */}
          <div className="hidden md:flex flex-1 max-w-xl">
            <GlobalSearch currentPincode={currentPincode} />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Admin Link */}
            {isAdmin && (
              <Link to="/admin" data-testid="admin-link">
                <Button variant="ghost" size="icon" className="hidden md:flex">
                  <LayoutDashboard className="w-5 h-5" />
                </Button>
              </Link>
            )}

            {/* User Menu */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="hidden md:flex" data-testid="user-menu-trigger">
                    <User className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5 text-sm font-medium">{user?.name}</div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-link">
                    <User className="w-4 h-4 mr-2" />
                    {t('profile')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/orders')} data-testid="orders-link">
                    <Package className="w-4 h-4 mr-2" />
                    {t('myOrders')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" className="hidden md:block" data-testid="login-link">
                <Button variant="outline" className="border-green-600 text-green-600 hover:bg-green-50">
                  {t('login')}
                </Button>
              </Link>
            )}

            {/* Cart */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="cart-trigger">
                  <ShoppingCart className="w-5 h-5" />
                  {cartCount > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-green-600" data-testid="cart-count">
                      {cartCount}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent className="w-full sm:max-w-md">
                <SheetHeader>
                  <SheetTitle>{t('yourCart')}</SheetTitle>
                </SheetHeader>
                <CartSheet />
              </SheetContent>
            </Sheet>

            {/* Mobile Menu */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Search */}
        <div className="md:hidden pb-4">
          <GlobalSearch currentPincode={currentPincode} />
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-slate-100 bg-white animate-slide-up" data-testid="mobile-menu">
          <div className="container-app py-4 space-y-2">
            <Link
              to="/"
              className="block px-4 py-2 rounded-lg hover:bg-slate-50"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('home')}
            </Link>
            <Link
              to="/products"
              className="block px-4 py-2 rounded-lg hover:bg-slate-50"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('allProducts')}
            </Link>
            {isAuthenticated ? (
              <>
                <Link
                  to="/profile"
                  className="block px-4 py-2 rounded-lg hover:bg-slate-50"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('profile')}
                </Link>
                <Link
                  to="/orders"
                  className="block px-4 py-2 rounded-lg hover:bg-slate-50"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('myOrders')}
                </Link>
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="block px-4 py-2 rounded-lg hover:bg-slate-50"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('dashboard')}
                  </Link>
                )}
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 rounded-lg hover:bg-slate-50 text-red-600"
                >
                  {t('logout')}
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="block px-4 py-2 rounded-lg bg-green-600 text-white text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('login')}
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Pincode Modal */}
      <PincodeModal open={pincodeModalOpen} onClose={() => setPincodeModalOpen(false)} />
    </header>
  );
};

export default Header;
